import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faCamera, 
  faImage,
  faExclamationTriangle,
  faTrash,
  faSignOutAlt
} from '@fortawesome/free-solid-svg-icons';

// Base Modal Component
const BaseModal = ({ isOpen, onClose, children, theme }) => {
  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(5px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem',
    },
    modal: {
      background: theme?.background?.card,
      borderRadius: '1.5rem',
      padding: '2rem',
      maxWidth: '400px',
      width: '100%',
      boxShadow: theme?.shadows?.xl,
      border: `1px solid ${theme?.borders?.primary}`,
      position: 'relative',
    },
    closeButton: {
      position: 'absolute',
      top: '1rem',
      right: '1rem',
      width: '2rem',
      height: '2rem',
      borderRadius: '0.5rem',
      border: 'none',
      background: theme?.backgrounds?.secondary,
      color: theme?.text?.secondary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          style={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            style={styles.modal}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              style={styles.closeButton}
              onClick={onClose}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Image Picker Modal
export const ImagePickerModal = ({ isOpen, onClose, onCamera, onGallery, theme }) => {
  const styles = {
    title: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: theme?.text?.primary,
      textAlign: 'center',
      marginBottom: '2rem',
    },
    optionsGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1rem',
    },
    optionButton: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '1.5rem',
      borderRadius: '0.75rem',
      border: `2px solid ${theme?.borders?.primary}`,
      background: theme?.backgrounds?.secondary,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    optionIcon: {
      fontSize: '2rem',
      color: theme?.primary?.rose?.[500] || '#f43f5e',
    },
    optionText: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: theme?.text?.primary,
    },
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} theme={theme}>
      <h2 style={styles.title}>Choose Photo Source</h2>
      
      <div style={styles.optionsGrid}>
        <motion.button
          style={styles.optionButton}
          whileHover={{ 
            scale: 1.05,
            borderColor: theme?.primary?.rose?.[500] || '#f43f5e',
          }}
          whileTap={{ scale: 0.95 }}
          onClick={onCamera}
        >
          <FontAwesomeIcon icon={faCamera} style={styles.optionIcon} />
          <span style={styles.optionText}>Camera</span>
        </motion.button>
        
        <motion.button
          style={styles.optionButton}
          whileHover={{ 
            scale: 1.05,
            borderColor: theme?.primary?.rose?.[500] || '#f43f5e',
          }}
          whileTap={{ scale: 0.95 }}
          onClick={onGallery}
        >
          <FontAwesomeIcon icon={faImage} style={styles.optionIcon} />
          <span style={styles.optionText}>Gallery</span>
        </motion.button>
      </div>
    </BaseModal>
  );
};

// Reset Data Modal
export const ResetDataModal = ({ isOpen, onClose, onConfirm, theme }) => {
  const styles = {
    iconContainer: {
      width: '4rem',
      height: '4rem',
      borderRadius: '1rem',
      background: `linear-gradient(135deg, ${theme?.dashboard?.status?.incorrect || '#ef4444'}20, ${theme?.dashboard?.status?.incorrect || '#ef4444'}40)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1.5rem',
    },
    icon: {
      fontSize: '2rem',
      color: theme?.dashboard?.status?.incorrect || '#ef4444',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: theme?.text?.primary,
      textAlign: 'center',
      marginBottom: '0.75rem',
    },
    description: {
      fontSize: '0.875rem',
      color: theme?.text?.secondary,
      textAlign: 'center',
      lineHeight: 1.6,
      marginBottom: '2rem',
    },
    buttonContainer: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'center',
    },
    button: {
      padding: '0.75rem 1.5rem',
      borderRadius: '0.5rem',
      border: 'none',
      fontSize: '0.875rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    cancelButton: {
      background: theme?.backgrounds?.secondary,
      color: theme?.text?.primary,
      border: `1px solid ${theme?.borders?.primary}`,
    },
    confirmButton: {
      background: theme?.dashboard?.status?.incorrect || '#ef4444',
      color: 'white',
    },
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} theme={theme}>
      <motion.div
        style={styles.iconContainer}
        animate={{ 
          rotate: [0, -5, 5, 0],
        }}
        transition={{ 
          duration: 2,
          repeat: Infinity,
          repeatDelay: 1,
        }}
      >
        <FontAwesomeIcon icon={faExclamationTriangle} style={styles.icon} />
      </motion.div>
      
      <h2 style={styles.title}>Reset App Data?</h2>
      <p style={styles.description}>
        This will permanently delete all your progress, bookmarks, and test history. 
        This action cannot be undone.
      </p>
      
      <div style={styles.buttonContainer}>
        <motion.button
          style={{ ...styles.button, ...styles.cancelButton }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onClose}
        >
          Cancel
        </motion.button>
        <motion.button
          style={{ ...styles.button, ...styles.confirmButton }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onConfirm}
        >
          Reset Data
        </motion.button>
      </div>
    </BaseModal>
  );
};

// Delete Account Modal
export const DeleteAccountModal = ({ isOpen, onClose, onConfirm, theme }) => {
  const styles = {
    iconContainer: {
      width: '4rem',
      height: '4rem',
      borderRadius: '1rem',
      background: `linear-gradient(135deg, ${theme?.dashboard?.status?.incorrect || '#ef4444'}20, ${theme?.dashboard?.status?.incorrect || '#ef4444'}40)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1.5rem',
    },
    icon: {
      fontSize: '2rem',
      color: theme?.dashboard?.status?.incorrect || '#ef4444',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: theme?.text?.primary,
      textAlign: 'center',
      marginBottom: '0.75rem',
    },
    description: {
      fontSize: '0.875rem',
      color: theme?.text?.secondary,
      textAlign: 'center',
      lineHeight: 1.6,
      marginBottom: '2rem',
    },
    buttonContainer: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'center',
    },
    button: {
      padding: '0.75rem 1.5rem',
      borderRadius: '0.5rem',
      border: 'none',
      fontSize: '0.875rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    cancelButton: {
      background: theme?.backgrounds?.secondary,
      color: theme?.text?.primary,
      border: `1px solid ${theme?.borders?.primary}`,
    },
    confirmButton: {
      background: theme?.dashboard?.status?.incorrect || '#ef4444',
      color: 'white',
    },
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} theme={theme}>
      <motion.div
        style={styles.iconContainer}
        animate={{
          rotate: [0, -5, 5, 0],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          repeatDelay: 1,
        }}
      >
        <FontAwesomeIcon icon={faExclamationTriangle} style={styles.icon} />
      </motion.div>

      <h2 style={styles.title}>Delete Account?</h2>
      <p style={styles.description}>
        This will permanently delete your account and all associated data.
        This process is irreversible and cannot be undone.
      </p>

      <div style={styles.buttonContainer}>
        <motion.button
          style={{ ...styles.button, ...styles.cancelButton }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onClose}
        >
          Cancel
        </motion.button>
        <motion.button
          style={{ ...styles.button, ...styles.confirmButton }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onConfirm}
        >
          Delete Account
        </motion.button>
      </div>
    </BaseModal>
  );
};

// Sign Out Modal
export const SignOutModal = ({ isOpen, onClose, onConfirm, theme }) => {
  const styles = {
    iconContainer: {
      width: '4rem',
      height: '4rem',
      borderRadius: '1rem',
      background: `linear-gradient(135deg, ${theme?.primary?.gray?.[400] || '#9ca3af'}20, ${theme?.primary?.gray?.[400] || '#9ca3af'}40)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1.5rem',
    },
    icon: {
      fontSize: '2rem',
      color: theme?.primary?.gray?.[600] || '#6b7280',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: theme?.text?.primary,
      textAlign: 'center',
      marginBottom: '0.75rem',
    },
    description: {
      fontSize: '0.875rem',
      color: theme?.text?.secondary,
      textAlign: 'center',
      lineHeight: 1.6,
      marginBottom: '2rem',
    },
    buttonContainer: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'center',
    },
    button: {
      padding: '0.75rem 1.5rem',
      borderRadius: '0.5rem',
      border: 'none',
      fontSize: '0.875rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    cancelButton: {
      background: theme?.backgrounds?.secondary,
      color: theme?.text?.primary,
      border: `1px solid ${theme?.borders?.primary}`,
    },
    confirmButton: {
      background: theme?.primary?.gray?.[500] || '#6b7280',
      color: 'white',
    },
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} theme={theme}>
      <motion.div
        style={styles.iconContainer}
        animate={{ 
          rotate: [0, -5, 5, 0],
        }}
        transition={{ 
          duration: 2,
          repeat: Infinity,
          repeatDelay: 1,
        }}
      >
        <FontAwesomeIcon icon={faSignOutAlt} style={styles.icon} />
      </motion.div>
      
      <h2 style={styles.title}>Sign Out?</h2>
      <p style={styles.description}>
        Are you sure you want to sign out? You'll need to sign in again to access your account.
      </p>
      
      <div style={styles.buttonContainer}>
        <motion.button
          style={{ ...styles.button, ...styles.cancelButton }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onClose}
        >
          Cancel
        </motion.button>
        <motion.button
          style={{ ...styles.button, ...styles.confirmButton }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onConfirm}
        >
          Sign Out
        </motion.button>
      </div>
    </BaseModal>
  );
};

const ProfileModals = {
  ImagePickerModal,
  ResetDataModal,
  DeleteAccountModal,
  SignOutModal
};

export default ProfileModals;